import { ReactNode } from "react";
import {
  Circle,
  FontSizeTokens,
  Text,
  Token,
  XStack,
  XStackProps,
} from "tamagui";

interface StatusPillThemeProps {
  outlined?: boolean;
  error?: boolean;
  info?: boolean;
  success?: boolean;
  waiting?: boolean;
  warning?: boolean;
}

export interface StatusPillProps extends XStackProps, StatusPillThemeProps {
  fontSize?: FontSizeTokens;
  hideDot?: boolean;
  children: ReactNode;
}

export const StatusPill = ({
  outlined,
  error,
  info,
  success,
  waiting,
  warning,
  fontSize = "$4",
  hideDot,
  children,
  ...rest
}: StatusPillProps) => {
  const { backgroundColor, dotColor, textColor } = getThemeTokens({
    outlined,
    error,
    info,
    success,
    waiting,
    warning,
  });

  return (
    <XStack
      width="fit-content"
      maxWidth="100%"
      gap="$2"
      alignItems="center"
      backgroundColor={backgroundColor}
      paddingVertical="$1"
      paddingHorizontal="$2"
      borderWidth={2}
      borderColor="$white5"
      borderRadius="$3"
      {...rest}
    >
      {!hideDot && <Circle size={8} backgroundColor={dotColor} />}
      <Text
        fontSize={fontSize}
        color={textColor}
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {children}
      </Text>
    </XStack>
  );
};

const getThemeTokens = ({
  outlined,
  error,
  info,
  success,
  waiting,
  warning,
}: StatusPillThemeProps): {
  backgroundColor: Token;
  dotColor: Token;
  textColor: Token;
} => {
  if (error) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$warningRed" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$warningRed" as Token,
          dotColor: "$red3" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (info) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$hgPrimary" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$hgPrimary" as Token,
          dotColor: "$blue3" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (success) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$successGreen" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$successGreen" as Token,
          dotColor: "$green3" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (waiting) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$yellow11" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$yellow11" as Token,
          dotColor: "$yellow3" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (warning) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$warning" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$warning" as Token,
          dotColor: "$orange3" as Token,
          textColor: "$black12" as Token,
        };
  }

  return outlined
    ? {
        backgroundColor: "$transparentBorder" as Token,
        dotColor: "$gray9" as Token,
        textColor: "$white12" as Token,
      }
    : {
        backgroundColor: "$gray12" as Token,
        dotColor: "$gray3" as Token,
        textColor: "$black12" as Token,
      };
};
